// FeatureWindowHost — mounts the floating feature windows (and any popped-out
// OS windows) for the resolved feature-window surface (featureWindowSurface.ts
// — covers every domain, not just one pilot tab), INSIDE the viewer's
// `relative` container (ParametricPreviewSection). This is the viewport half
// of the mechanism: the tree (FeatureTreeWindowItems) toggles which window is
// open; this renders it here over the 3D view.
//
// Flag-gated (`feature-tree-windows`, default ON — see lib/featureFlags.ts) as
// a kill-switch: flipped OFF, this renders NOTHING and every domain falls back
// to its legacy single pane. Renders the open feature's FloatingFeatureWindow,
// plus a PopoutWindow for each feature the user detached.
//
// Kept out of the always-loaded path — it's imported only by
// ParametricPreviewSection, which is already behind the Lazy3D boundary.

import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useHomepage } from '@/contexts/homepage-context';
import { PopoutWindow } from '@/components/suspensionKinematics/PopoutWindow';
import { FloatingFeatureWindow } from './FloatingFeatureWindow';
import { useFeatureWindows } from './useFeatureWindows';
import { featureWindowItems } from '@/components/featureTreeSidebar/featureWindowRegistry';
import { resolveFeatureWindowSurface } from '@/components/featureTreeSidebar/featureWindowSurface';

export function FeatureWindowHost() {
  const windowsEnabled = useFeatureFlag('feature-tree-windows', false);
  const {
    vehicleEnabled,
    vehicleTab,
    vehicleSubTab,
    textToCadSelected,
    implicitPrimitive,
  } = useHomepage();
  const { openKey, poppedOut, closeFeature, popOut } = useFeatureWindows();

  // Flag OFF, or no registered windows for this surface → render nothing (the
  // legacy single pane owns the params). Hooks above always run (rules of
  // hooks); the early return is after them.
  if (!windowsEnabled) return null;

  const surface = resolveFeatureWindowSurface({
    vehicleEnabled,
    vehicleTab,
    vehicleSubTab,
    textToCadSelected,
    implicitPrimitive,
  });
  const items = featureWindowItems(surface?.key);
  if (items.length === 0) return null;

  const enabled = vehicleEnabled;
  const openItem =
    openKey != null ? items.find((i) => i.key === openKey) : undefined;

  return (
    <>
      {/* In-viewer floating window for the open feature (unless popped out). */}
      {openItem && !poppedOut.has(openItem.key) && (
        <FloatingFeatureWindow
          title={openItem.label}
          width={openItem.width}
          onClose={() => closeFeature(openItem.key)}
          onPopOut={() => popOut(openItem.key, true)}
        >
          {openItem.render(enabled)}
        </FloatingFeatureWindow>
      )}

      {/* Popped-out features → their own OS window (rendered per popped key so
          switching the in-viewer window doesn't close a popped one). */}
      {[...poppedOut].map((key) => {
        const item = items.find((i) => i.key === key);
        if (!item) return null;
        return (
          <PopoutWindow
            key={key}
            title={item.label}
            width={item.width ?? 360}
            height={480}
            onClose={() => popOut(key, false)}
          >
            <div className="min-h-screen bg-telekinesis-bg-secondary-dark p-4 text-telekinesis-neutral-50">
              {item.render(enabled)}
            </div>
          </PopoutWindow>
        );
      })}
    </>
  );
}
